import { useCallback, useEffect, useRef, useState } from "react"
import { PrismButton } from "@prism-ui/react"
import { faXmark } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

/**
 * js version of hooks/useClickOutside (can't import *.ts into *.jsx)
 */
const useClickOutside = (cb) => {
  const ref = useRef(null)

  const handleClickOutside = useCallback((event) => {
    if (ref.current && !ref.current.contains(event.target)) cb()
  }, [cb])

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true)
    return () => document.removeEventListener('click', handleClickOutside, true)
  }, [handleClickOutside])

  return ref
}

/**
 * Compact two-state delete affordance built on Prism's button.
 * First activation reveals a solid danger "confirm" button; a second activation
 * fires onDelete. Blur, Escape, or a click outside resets to the icon state.
 */
const ConfirmDeleteButton = ({ onDelete, disabled, confirmLabel = "Delete" }) => {
  const [confirm, setConfirm] = useState(false)

  const ref = useClickOutside(() => {
    setConfirm(false)
  })

  const handleClick = () => {
    if (disabled) return
    if (!confirm) {
      setConfirm(true)
      return
    }
    if (onDelete) onDelete()
  }

  const handleKeyUp = (e) => {
    if (e.key === "Escape") setConfirm(false)
  }

  return (
    // The wrapper owns keyboard reset and stops the native click from bubbling
    // to row/parent handlers (Prism's prismClick carries the app logic).
    <span
      ref={ref}
      className="confirm-delete"
      onClick={(e) => e.stopPropagation()}
      onKeyUp={handleKeyUp}
    >
      <PrismButton
        tone="danger"
        size="sm"
        spacing="compact"
        display={confirm ? "fill" : "ghost-icon"}
        disabled={disabled}
        setAriaLabel={confirm ? confirmLabel : "Delete"}
        onPrismClick={handleClick}
        onPrismBlur={() => setConfirm(false)}
      >
        {/* Default slot (a v9 ghost-icon button projects no named slots) and an explicit size
            (an unsized FA svg collapses to 0×0 inside the shrink-to-fit shadow button). */}
        {confirm ? confirmLabel : <FontAwesomeIcon icon={faXmark} style={{ width: "1em", height: "1em" }} />}
      </PrismButton>
    </span>
  )
}

export default ConfirmDeleteButton
